/* prettier-ignore */

import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { NewCustomRouteService } from '../new-custom-route/new-custom-route.service';
import { ToolsService } from 'src/app/services/tools.service';
import { RestService } from 'src/app/services/rest.service';
import { DataChatService } from 'src/chatbot/service/dataChat.service';
import { TranslationService } from 'src/app/services/translation.service';
import { AuthService } from 'src/app/auth/services';
import { combineLatest, filter, take } from 'rxjs';
import { DebugService } from 'src/app/services/debug.service';
import { StatisticsService } from 'src/app/services/statistics.service';

@Injectable({providedIn: 'root'})

export class SendUpdateRouteService {
    private debugService = inject(DebugService);
    private statsService = inject(StatisticsService);
    private http = inject(HttpClient);
    private routeService = inject(NewCustomRouteService);
    private rest = inject(RestService);
    private dataChatService = inject(DataChatService);
    private _translationService = inject(TranslationService);
    private _selectedLanguage = this._translationService.selectedLanguage;
    private tools = inject(ToolsService);
    private authService = inject(AuthService);
    private initializedData = signal(false);
    public userData = signal({ id: null, info: null, pdfs: [] as any});
    public showAddPDFsList = signal(false);
    public allPdfs = signal([]);
    public pdfsByCategories = signal([]);
    public survey = signal(null);
    public registerBeforeQuestion = signal(null);
    public registerBefore = signal(null);
    public registerAfterQuestion = signal(null)
    public registerAfter = signal(null);
    public item: any = signal({
        id: 0,
        name: {},
        route_days: 0,
        image: null
    })

    public showRegisterBeforeError = signal(false);
    public showRegisterAfterError = signal(false);

    // constructor(){
    //     this.loadInitialData();
    // }

    public loadInitialData() {
        if (this.initializedData())
            return;

        combineLatest([this.tools.isApp$, this.tools.isDistributor$])
        .pipe(
          filter(
            ([isApp, isDistributor]) => isApp !== null || isDistributor !== null
          ),
          take(1)
        )
        .subscribe(() => {
          const token = this.statsService.token;
          this.getData(token);
        });
    }

    // En modo registro por canal (tech-register-by-token) isApp$/isDistributor$ nunca emiten,
    // así que se carga la lista de PDFs directamente (HRS-2909)
    public loadPdfsForRegister() {
        if (this.initializedData())
            return;

        this.getData();
    }

    getData(token = null) {
        const isDistributor = this.tools.isDistributor ?? this.rest.device()?.type === 'distributor';
        const relativeEndPointPath = isDistributor ? 'get-pdfs-community-list' : 'get-pdfs-list';
        const url =  `${this.rest.apiUrlBase}/api/${relativeEndPointPath}/${this.rest.subDomain}${token ? `/${token}` : ''}`;

        this.http.get(url).subscribe(
            (response: any) => {
                this.debugService.debug(response, '[SendUpdateRouteService] SendRoute API Response');
                this.pdfsByCategories.set(response?.["pdfs-categories"] || []);
                this.allPdfs.set(response?.pdfs || []); //response?.pdfs?.filter(pdf => this.userData()?.pdfs?.includes(pdf.id));
                this.survey.set(response?.survey || null);
                this.registerAfterQuestion.set(response?.registerAfter);
                this.initializedData.set(true);
            },
            (error) => this.debugService.error(error, '[SendUpdateRouteService] Error getting sendRoute data')
        );
    }

    resetRouteCard(){
        this.item.set({
            id: 0,
            name: {},
            route_days: 0,
            image: null
        })
    }

    resetUserData(){
        this.userData.update(prev => ({ id: null, info: null, pdfs: [] as any}));
    }

    removePdf(pdfId){
        this.userData.update(prev => ({...prev, pdfs: prev?.pdfs?.filter(id => id !== pdfId)}));
    }

    addPdf(pdfId){
        this.userData.update(prev => ({...prev, pdfs: [...prev.pdfs, pdfId]}));
    }

    toggleView(){
        this.showAddPDFsList.update(prev => !prev);
    }

    private generateSaveStructure(formValue){
        const categories = this.routeService.routeConfig?.categories?.map(category => category.id) || [];
        const subcategories = this.routeService.routeConfig?.categories?.map(category => category.subcategories)?.flat()
        const routeStructure = this.routeService.generateSendStructure();
        const domain = window.location.origin;

        this.userData.update(prev => ({...prev,  info: formValue.additionalInfo}))
        return {
            distributorID: this.rest.device()?.distributorID,
            clientID: this.rest.device()?.client,
            deviceID: this.rest.device()?.device ?? 0,
            channelID: this.rest.device()?.channel,
            route: routeStructure,
            days: routeStructure?.length,
            nombre: this.authService.user()?.name ?? this.authService.user()?.email ?? formValue.name,
            email: this.authService.user()?.email ?? formValue.email,
            info: formValue.additionalInfo,
            language: this._translationService.displayLanguage(),
            pdfs: this.userData()?.pdfs,
            recommended: formValue.isRecommendedRoute ?? 0,
            name_route: formValue?.routeName,
            users_webapp_id: this.authService.user()?.id ?? null,
            travelerType: this.routeService.routeConfig?.travellerType,
            categories: categories,
            subCategories: subcategories,
            additionalInfo: {
                registerBefore: this.registerBefore(),
                registerAfter: this.registerAfter()
            },
            appID: this.rest.device()?.appID,
            type: this.rest.device()?.type,
            owner: this.rest.device()?.type == 'distributor' ? 'distributor' : 'client',
            ...this.statsService.cmsMetricsIdentity(),
            image: formValue?.routeImage,
            withoutRoute: this.tools.isInfoRoutePage(),
            domain
        }
    }

    generateUpdateStructure(formValue){
        const routeStructure = this.routeService.generateSendStructure();
        return {
            routeID: formValue?.id ?? this.userData()?.id,
            route: routeStructure,
            days: routeStructure?.length,
            info: formValue?.additionalInfo,
            pdfs: this.userData()?.pdfs,
            name_route: formValue?.routeName,
            image: formValue?.routeImage,
            recommended: formValue?.isRecommendedRoute ?? 0,
        }
    }

    saveRoute(formValue): any {
        const data = this.generateSaveStructure(formValue);
        const formData = new FormData();
        formData.append("data", JSON.stringify(data));
        return this.http.post(`${this.rest.apiUrlBase}/api/pwa-save-route`, formData);
    }

    updatedRoute(formValue = null): any {
        this.userData.update(prev => ({...prev, info: formValue?.additionalInfo}))
        const data = this.generateUpdateStructure(formValue);
        const formData = new FormData();
        formData.append("data", JSON.stringify(data));
        return this.http.post(`${this.rest.apiUrlBase}/api/pwa-update-saved-route`, formData);
    }

    checkRequiredRegisterBefore(){
        return this.registerBeforeQuestion()?.[this._selectedLanguage()]?.filter(answer => answer.required)
    }

    isErrorFromAnswer(registerAnswer){
        return registerAnswer?.filter(answer => answer.required)?.some(data => !data.answer || data.answer?.length === 0 || data.answer === '');
    }

    isErrorFromRegisterBefore(){
        if (this.authService.user()?.id)
            return false;

        if (!this.registerBeforeQuestion()?.[this._selectedLanguage()]?.some(answer => answer.required))
            return false;

        if (this.registerBefore())
            return this.registerBefore()?.filter(answer => answer.required)?.some(data => !data.answer || data.answer?.length === 0 || data.answer === '');

        return true;
    }

    isErrorFromRegisterAfter(){
        if (this.authService.user()?.id)
            return false;

        if (!this.registerAfterQuestion()?.[this._selectedLanguage()]?.some(answer => answer.required))
            return false;

        if (this.registerAfter())
            return this.registerAfter()?.filter(answer => answer.required)?.some(data => !data.answer || data.answer?.length === 0 || data.answer === '');

        return true;
    }
}